import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import CollectionAPI from "../../api/collectionAPI";
import AuthLayout from "../../components/AuthLayout/AuthLayout";
import Pagination from "../../components/Pagination";
import Filter from "../../components/Filter/Filter";
import { setMessage } from "../../redux/actions/message";
import { MEDIA_URL } from "../../configs";
import Dropdown from "react-bootstrap/Dropdown";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Sorter from "../../components/Sorter/Sorter";
import format from "../../helpers/format";
import dayjs from "dayjs";

const filters = [
  {
    type: "text",
    name: "search",
    placeholder: "Search by name or tags",
    defaultValue: "",
  },
  {
    type: "text",
    name: "owner",
    placeholder: "Username",
    defaultValue: "",
  },
  {
    type: "select",
    name: "status",
    options: [
      { title: "All Statuses", value: "" },
      { title: "Active", value: "active" },
      { title: "Deleted", value: "deleted" },
    ],
    defaultValue: "",
  },
  {
    type: "select",
    name: "private",
    options: [
      { title: "Private", value: "" },
      { title: "Yes", value: "true" },
      { title: "No", value: "false" },
    ],
    defaultValue: "",
  },
];

const defaultFilter = filters.reduce(
  (obj, cur) => ({ ...obj, [cur.name]: cur.defaultValue }),
  {}
);

const initialQuery = {
  ...defaultFilter,
  sortby: "",
  direction: "",
  page: "",
};

const pageSize = 50;

const CollectionList = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState(initialQuery);
  const [fullQuery, setFullQuery] = useState({ ...initialQuery, pageSize });

  // Bulk selection and update
  const [selectedIds, setSelectedIds] = useState([]);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateStatus, setUpdateStatus] = useState("");
  const [updatePrivate, setUpdatePrivate] = useState("");

  // Fetch collections from the API
  const fetchCollections = async (query = "") => {
    setLoading(true);
    setError(null);
    try {
      const data = await CollectionAPI.getAllCollections(query);
      // Log first collection thumbnail for debugging
      if (data.items && data.items[0] && data.items[0].thumbnail) {
      }
      setCollections(data.items);
      setTotal(data.size);
    } catch (err) {
      setError("Failed to fetch collections. Please try again.");
      dispatch(setMessage(err.message, "danger"));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setQuery(initialQuery);
    setFullQuery({ ...initialQuery, pageSize });
  };

  useEffect(() => {
    (async () => {
      const newQuery = { ...query };

      Object.keys(defaultFilter).forEach((filter) => {
        if (searchParams.get(filter)) {
          newQuery[filter] = searchParams.get(filter);
        }
      });

      if (searchParams.get("sortby")) {
        newQuery.sortby = searchParams.get("sortby");
      }
      if (searchParams.get("direction")) {
        newQuery.direction = searchParams.get("direction");
      }
      
      if (!searchParams.get("page")) {
        newQuery.page = 1;
        setCurrentPage(1);
      } else {
        newQuery.page = searchParams.get("page");
        setCurrentPage(parseInt(searchParams.get("page"), 10));
      }
      
      setQuery(newQuery);

      const fullQuery = { ...newQuery, pageSize };
      setFullQuery(fullQuery);

      await fetchCollections(fullQuery);
    })();
  }, [location]);

  // Checkbox handlers
  const handleSelectAll = () => {
    if (selectedIds.length === collections.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(collections.map((c) => c._id));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  // Bulk update handlers
  const handleBulkUpdate = async (e) => {
    e.preventDefault();
    if (!updateStatus && updatePrivate === "") {
      dispatch(setMessage("Please select at least one field to update.", "danger"));
      return;
    }
    try {
      await CollectionAPI.bulkUpdateCollections({
        collection_ids: selectedIds,
        ...(updateStatus && { status: updateStatus }),
        ...(updatePrivate !== "" && { private: updatePrivate === "true" }),
      });
      setShowUpdateModal(false);
      setSelectedIds([]);
      setUpdateStatus("");
      setUpdatePrivate("");
      dispatch(setMessage("Collections updated successfully.", "success"));
      await fetchCollections(fullQuery);
    } catch (err) {
      dispatch(setMessage("Bulk update failed.", "danger"));
    }
  };

  return (
    <AuthLayout>
      <header className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fs-5">Collections</h2>
        <div className="d-flex align-self-end">
        <Link className="btn btn-primary px-4 me-2" to={`/collections/new`}>
          <i className="bi bi-plus-lg me-2"></i>
          Create Collection
        </Link>
        <Dropdown>
          <Dropdown.Toggle variant="outline-primary" id="dropdown-basic">
            <i className="bi bi-check"></i>
            <span className="ms-2">Action</span>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item
              onClick={() => {
                if (selectedIds.length === 0) {
                  dispatch(setMessage("Please select at least one collection.", "danger"));
                  return;
                }
                setShowUpdateModal(true);
              }}
            >
              Update
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
        </div>
      </header>
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <Filter
          defaultFilter={defaultFilter}
          filters={filters}
          reset={handleReset}
        />
        
      </div>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      {loading ? (
        <p className="text-center p-5">Loading...</p>
      ) : (
        <>
          {collections && collections.length > 0 ? (
            
            <>
              <table className="table table-hover mb-4">
                <thead>
                  <tr>
                    <th scope="col" className="fw-normal text-muted">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === collections.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th scope="col" className="fw-normal text-muted">#</th>
                    <th scope="col" className="fw-normal text-muted">
                      <Sorter
                        defaultFilter={defaultFilter}
                        query={query}
                        title="Name"
                        name="collection_name"
                        sorterHandle={(name, direction) => {
                          setQuery((prev) => ({
                            ...prev,
                            sortby: name,
                            direction: direction,
                            page: 1,
                          }));
                        }}
                      />
                    </th>
                    {/* <th scope="col" className="fw-normal text-muted">U</th> */}
                    <th scope="col" className="fw-normal text-muted">Status</th>
                    <th scope="col" className="fw-normal text-muted">Resources</th>
                    <th scope="col" className="fw-normal text-muted">Private</th>
                    <th scope="col" className="fw-normal text-muted">
                      <Sorter
                        defaultFilter={defaultFilter}
                        query={query}
                        title="Created At"
                        name="created_at"
                        sorterHandle={(name, direction) => {
                          setQuery((prev) => ({
                            ...prev,
                            sortby: name,
                            direction: direction,
                            page: 1,
                          }));
                        }}
                      />
                    </th>
                    <th scope="col" className="fw-normal text-muted">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {collections.map((collection, idx) => (
                    
                    <tr key={collection._id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(collection._id)}
                          onChange={() => handleSelectOne(collection._id)}
                        />
                      </td>
                      <td>
                        
                        {collection.thumbnail ? (
                          <img
                            src={`${MEDIA_URL}${collection.thumbnail.path}`}
                            alt={collection.collection_name}
                            style={{
                              width: 48,
                              height: 48,
                              objectFit: "cover",
                              borderRadius: 6,
                              marginRight: 8,
                            }}
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            onError={e => {
                              console.error('Image failed to load:', `${MEDIA_URL}${collection.thumbnail.path}`);
                              console.error('Error details:', {
                                event: e,
                                naturalWidth: e.target.naturalWidth,
                                naturalHeight: e.target.naturalHeight,
                                complete: e.target.complete,
                                src: e.target.src
                              });
                              
                              // Test if URL is accessible via fetch
                              fetch(`${MEDIA_URL}${collection.thumbnail.path}`, { method: 'HEAD', mode: 'no-cors' })
                                .catch(err => console.error('URL fetch failed:', err));
                              
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                            // onLoad={() => {
                            // }}
                          />
                        ) : (
                          <span className="d-inline-block text-center" style={{ width: 48, height: 48, lineHeight: '48px' }}>
                            <i className="bi bi-image text-muted fs-3 opacity-50" title="No image"></i>
                          </span>
                        )}
                      </td>
                      <td>
                        {/* {`${MEDIA_URL}${collection.thumbnail.path}`} */}
                        <Link to={`/collections/view/${collection._id}`}>
                          <strong className="rname">{collection.collection_name}</strong>
                        </Link>
                        <small className="small text-muted d-block">
                          {collection._id}
                        </small>
                      </td>
                      <td>{format.formatResourceStatus(collection.status)}</td>
                      <td>
                        <span className={`badge rounded-pill fw-normal ${collection.resource_count === 0 ? 'bg-danger bg-opacity-10 text-danger' : 'bg-secondary bg-opacity-10 text-secondary'}`}>
                          {collection.resource_count}
                        </span>
                      </td>
                      <td>
                        {collection.private ? (
                          <>
                            <i className="bi bi-lock-fill text-muted" title="Private" aria-label="Private"></i>
                            <span className="visually-hidden">Private</span>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-unlock" title="Public" aria-label="Public"></i>
                            <span className="visually-hidden">Public</span>
                          </>
                        )}
                      </td>
                      <td><span className="small text-muted">{dayjs(collection.created_at).format("YYYY-MM-DD HH:mm:ss")}</span></td>
                      <td>
                        <div className="button-group">
                          <Link
                            to={`/collections/view/${collection._id}`}
                            className="btn btn-sm"
                          >
                            <i className="bi bi-eye text-muted"></i>
                          </Link>
                          <Link
                            to={`/collections/edit/${collection._id}`}
                            className="btn btn-sm"
                          >
                            <i className="bi bi-pencil text-muted"></i>
                          </Link>
                          {/* <Link
                            to={`/oders/delete/${index}`}
                            className="btn btn-sm"
                          >
                            
                            <i className="bi bi-trash-fill text-muted"></i>
                          </Link> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                count={total}
                currentPage={currentPage}
                pageSize={pageSize}
                path={"collections"}
                moduleName={"collection"}
                query={query}
              />
            </>
          ) : (
            <p className="my-5 py-2 text-center bg-warning bg-opacity-10 text-warning rounded">
              No collections found.
            </p>
          )}
        </>
      )}

      {/* Bulk Update Modal */}
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Bulk Update Collections</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleBulkUpdate}>
          <Modal.Body>
            <div className="mb-3">
              <label>Status</label>
              <select
                className="form-select"
                value={updateStatus}
                onChange={(e) => setUpdateStatus(e.target.value)}
              >
                <option value="">-- Select Status --</option>
                <option value="active">Active</option>
                <option value="deleted">Deleted</option>
              </select>
            </div>
            <div className="mb-3">
              <label>Private</label>
              <select
                className="form-select"
                value={updatePrivate}
                onChange={(e) => setUpdatePrivate(e.target.value)}
              >
                <option value="">-- Select --</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </AuthLayout>
  );
};

export default CollectionList;